import {
  RadioGroup as AriakitRadioGroup, useRadioStore,
} from '@ariakit/react';
import classNames from 'classnames';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import getClassNames from 'src/common/utils/getClassNames';
import { RadioContextProvider } from '../RadioContext';
import styles from './RadioGroup.module.scss';
import { parseRadioValue } from '../Radio';

export type RadioGroupProps = {
  children: ReactNode;
  defaultValue?: string | number | boolean | null;
  value?: string | number | boolean | null;
  wrap?: boolean;
  alignCenter?: boolean;
  orientation?: 'horizontal' | 'vertical';
  onChange?: (value: string | number | boolean | null) => void;
  disabled?: boolean;
  variant?: 'default' | 'pills';
  fullWidth?: boolean;
  minContent?: boolean;
};
const RadioGroup: FC<RadioGroupProps> = ({
  defaultValue = null,
  value: externalValue,
  children,
  wrap,
  alignCenter = false,
  orientation = 'horizontal',
  onChange,
  disabled,
  variant = 'default',
  fullWidth = false,
  minContent = false,
}) => {
  const store = useRadioStore({ defaultValue: parseRadioValue(defaultValue ?? externalValue) });
  const [value, setValue] = useState(store.getState().value);
  const [focus, setFocus] = useState<string | number | boolean>();

  const setValueAndChange = useCallback((val: string | number | boolean | null) => {
    setValue((prev) => {
      const parsed = parseRadioValue(val);
      if (prev !== parsed) {
        onChange?.(val);
      }
      return parsed;
    });
  }, [onChange]);

  const updateValue = useCallback(() => {
    const val = store.getState().value;
    setValueAndChange(val);
  }, [setValueAndChange, store]);

  useEffect(() => {
    setValue(parseRadioValue(externalValue) ?? null);
    store.setValue(parseRadioValue(externalValue) ?? null);
  }, [externalValue, store]);

  return (
    <RadioContextProvider value={{
      value, focus, setFocus, setValue: setValueAndChange, disabled,
    }}
    >
      <AriakitRadioGroup
        store={store}
        className={getClassNames(styles, 'root', classNames(orientation, variant, {
          wrap, fullWidth, alignCenter, minContent,
        }))}
        onChange={updateValue}
        onBlur={() => setFocus(undefined)}
      >
        {children}
      </AriakitRadioGroup>
    </RadioContextProvider>
  );
};
export default RadioGroup;
